import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { formatPhoneNumber } from "@/lib/utils";
// import { toast } from "sonner";
import { useToast } from "@/hooks/use-toast";
import { Save } from "lucide-react";

interface CompanyInformationForm {
  name: string;
  website: string;
  business_phone: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  owner_name: string;
  owner_phone: string;
  owner_email: string;
}

const CompanyInformation: React.FC = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const selectedMover = useSelector(getSelectedMover);
  const [updateMoverApi] = useUpdateMoverMutation();

  const form = useForm<CompanyInformationForm>({
    defaultValues: {
      ...selectedMover,
      business_phone: formatPhoneNumber(selectedMover?.business_phone || ""),
      owner_phone: formatPhoneNumber(selectedMover?.owner_phone || ""),
    },
  });

  const onSubmit = async (data: CompanyInformationForm) => {
    try {
      const updatedMover = await updateMoverApi({
        data: {
          name: data.name,
          website: data.website,
          business_phone: data.business_phone,
          email: data.email,
          street: data.street,
          city: data.city,
          state: data.state,
          zip: data.zip,
          owner_name: data.owner_name,
          owner_phone: data.owner_phone,
          owner_email: data.owner_email,
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Company information updated successfully",
        variant: "default",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to update company information. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="max-w-screen-sm px-8 py-4 flex flex-col justify-start items-start gap-4">
          <h2 className="text-lg font-bold">{"Company Information"}</h2>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} className="w-full" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="w-full flex space-x-4">
            <FormField
              control={form.control}
              name="business_phone"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Business Phone</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) =>
                        field.onChange(formatPhoneNumber(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Business Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* website */}
          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div>
            <div className="flex flex-col md:flex-row gap-4">
              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Address</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Zipcode</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* <FormField
            control={form.control}
            name="owner_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Owner Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex space-x-4">
            <FormField
              control={form.control}
              name="owner_email"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Owner Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="owner_phone"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Owner Phone</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) =>
                        field.onChange(formatPhoneNumber(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div> */}

          <div className="bg-stone-50 fixed bottom-0 left-0 w-full p-4 border-t border-gray-200">
            <Button type="submit" size="lg">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default CompanyInformation;
